import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtDate } from "./shared";
import LeadDrawer from "./LeadDrawer";
import { Inbox, Copy, RefreshCw, CheckCircle2, XCircle, Mail, Zap, AlertTriangle, Clock, Send, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_BADGES = {
    ricevuta: "bg-ink-900 text-ink-400 border-white/10",
    elaborata: "bg-blue-900/30 text-blue-300 border-blue-700/40",
    lead_creato: "bg-green-900/30 text-green-300 border-green-700/40",
    duplicata: "bg-amber-900/30 text-amber-300 border-amber-700/40",
    errore: "bg-red-900/30 text-red-300 border-red-700/40",
};
const STATUS_LABELS = { ricevuta: "Ricevuta", elaborata: "Elaborata", lead_creato: "Lead creato", duplicata: "Duplicata", errore: "Errore" };

export default function CrmEmailParsing() {
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [openLead, setOpenLead] = useState(null);
    const [showTest, setShowTest] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    const load = useCallback(async () => {
        try { const r = await api.get("/crm/email-parsing/overview"); setData(r.data); } catch (e) { console.error(e); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="p-8 text-center text-ink-400 text-sm">Caricamento Email Parsing...</div>;
    const s = data.stats;
    const enabled = data.settings.enabled;

    const copyInbox = async () => {
        try { await navigator.clipboard.writeText(data.inbox); setCopied(true); setTimeout(() => setCopied(false), 2500); }
        catch { window.prompt("Copia l'indirizzo:", data.inbox); }
    };
    const regenerate = async () => {
        if (!window.confirm("Rigenerare l'indirizzo? Quello attuale smetterà di funzionare e dovrai aggiornare gli inoltri sui portali.")) return;
        await api.post("/crm/email-parsing/regenerate");
        load();
    };
    const toggleEnabled = async () => {
        await api.put("/crm/email-parsing/settings", { enabled: !enabled });
        load();
    };

    return (
        <div data-testid="email-parsing-tab" className="space-y-8">
            {/* Header: stato + inbox */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="border border-white/10 bg-ink-900/40 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-400"><Zap size={14} /> Stato servizio</div>
                        <button data-testid="ep-toggle" onClick={toggleEnabled} role="switch" aria-checked={enabled}
                            className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? "bg-gold" : "bg-ink-700"}`}>
                            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink-950 transition-transform ${enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                        </button>
                    </div>
                    <div data-testid="ep-status" className={`flex items-center gap-2 font-serif text-2xl ${enabled ? "text-green-300" : "text-ink-500"}`}>
                        {enabled ? <CheckCircle2 size={20} /> : <XCircle size={20} />} {enabled ? "Attivo" : "Disattivato"}
                    </div>
                    <p className="text-xs text-ink-400 mt-2">I lead ricevuti via email dai portali vengono importati automaticamente nel CRM. Nessuno scraping: elaboriamo solo email inoltrate volontariamente dalla tua agenzia.</p>
                </div>
                <div className="border border-gold/30 bg-gold/5 p-5">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-400 mb-3"><Inbox size={14} /> La tua email dedicata</div>
                    <div data-testid="ep-inbox" className="font-mono text-lg text-gold break-all">{data.inbox}</div>
                    <div className="flex gap-2 mt-4">
                        <button data-testid="ep-copy" onClick={copyInbox} className="cta-gold px-4 py-2 text-xs flex items-center gap-2">
                            <Copy size={13} /> {copied ? "Copiata!" : "📋 Copia Email"}
                        </button>
                        <button data-testid="ep-regenerate" onClick={regenerate} className="cta-outline px-4 py-2 text-xs flex items-center gap-2">
                            <RefreshCw size={13} /> Rigenera
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat icon={<Mail size={14} />} label="Email ricevute oggi" value={s.emails_today} testid="ep-stat-emails" />
                <Stat icon={<CheckCircle2 size={14} />} label="Lead creati oggi" value={s.leads_today} highlight testid="ep-stat-leads" />
                <Stat icon={<AlertTriangle size={14} />} label="Errori" value={s.errors} warn={s.errors > 0} testid="ep-stat-errors" />
                <Stat icon={<Clock size={14} />} label="Ultima sincronizzazione" value={s.last_sync ? fmtDate(s.last_sync) : "—"} small testid="ep-stat-sync" />
            </div>

            {/* Setup guidato */}
            <div className="border border-white/10 bg-ink-900/30 p-6">
                <h3 className="font-serif text-xl text-ink-50 mb-5">Configurazione guidata</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <Step n="1" title="Copia l'indirizzo dedicato" text="Usa il pulsante 📋 Copia Email qui sopra." />
                    <Step n="2" title="Imposta l'inoltro" text="Sui portali (Idealista, Immobiliare.it, Casa.it) imposta questo indirizzo come destinatario dei lead, oppure crea un inoltro automatico dalla tua casella verso l'indirizzo VendiDirect." />
                    <Step n="3" title="Tutto automatico" text="Ogni nuova email viene letta, i dati del cliente estratti con l'AI e trasformati in un Lead del CRM con origine del portale." />
                </div>
            </div>

            {/* Test simulazione */}
            <div className="border border-white/10 bg-ink-900/30">
                <button data-testid="ep-test-toggle" onClick={() => setShowTest(!showTest)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/5">
                    <span className="font-serif text-lg text-ink-50">Prova con un'email di test</span>
                    {showTest ? <ChevronUp size={15} className="text-ink-400" /> : <ChevronDown size={15} className="text-ink-400" />}
                </button>
                {showTest && <TestForm onDone={load} />}
            </div>

            {/* Impostazioni */}
            <div className="border border-white/10 bg-ink-900/30">
                <button data-testid="ep-settings-toggle" onClick={() => setShowSettings(!showSettings)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/5">
                    <span className="font-serif text-lg text-ink-50">Impostazioni</span>
                    {showSettings ? <ChevronUp size={15} className="text-ink-400" /> : <ChevronDown size={15} className="text-ink-400" />}
                </button>
                {showSettings && <SettingsPanel settings={data.settings} onSaved={load} />}
            </div>

            {/* Log */}
            <div>
                <h3 className="font-serif text-xl text-ink-50 mb-4">Log elaborazione</h3>
                <div className="border border-white/10 overflow-x-auto">
                    <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-ink-400 bg-white/5 px-4 py-2.5 min-w-[700px]">
                        <div className="col-span-2">Data · Ora</div>
                        <div className="col-span-3">Mittente</div>
                        <div className="col-span-3">Oggetto</div>
                        <div className="col-span-1">Portale</div>
                        <div className="col-span-2">Stato</div>
                        <div className="col-span-1 text-right">Azioni</div>
                    </div>
                    {data.logs.length === 0 && <div className="p-8 text-center text-xs text-ink-500">Nessuna email elaborata finora. Prova la simulazione qui sopra.</div>}
                    {data.logs.map((l) => (
                        <div key={l.id} data-testid={`ep-log-${l.id}`} className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center px-4 py-2.5 text-xs border-t border-white/5 min-w-[700px]">
                            <div className="md:col-span-2 text-ink-400">{fmtDate(l.received_at)}</div>
                            <div className="md:col-span-3 text-ink-100 truncate">{l.from_email || "—"}</div>
                            <div className="md:col-span-3 text-ink-300 truncate">{l.subject || "—"}</div>
                            <div className="md:col-span-1 text-ink-400">{data.portals[l.portal] || l.portal}</div>
                            <div className="md:col-span-2">
                                <span className={`text-[10px] uppercase px-2 py-0.5 border ${STATUS_BADGES[l.status] || STATUS_BADGES.ricevuta}`}>{STATUS_LABELS[l.status] || l.status}</span>
                                {l.error && <div className="text-[10px] text-red-400/80 truncate mt-0.5">{l.error}</div>}
                            </div>
                            <div className="md:col-span-1 text-right">
                                {l.lead_id && <button data-testid={`ep-open-lead-${l.id}`} onClick={() => setOpenLead(l.lead_id)} className="text-gold text-[11px] hover:underline">Apri lead</button>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {openLead && <LeadDrawer leadId={openLead} agents={[]} onClose={() => setOpenLead(null)} onChanged={load} />}
        </div>
    );
}

function Stat({ icon, label, value, highlight, warn, small, testid }) {
    return (
        <div data-testid={testid} className={`p-4 border ${highlight ? "border-green-700/40 bg-green-900/10" : warn ? "border-red-700/40 bg-red-900/10" : "border-white/10 bg-ink-900/40"}`}>
            <div className="flex items-center gap-2 text-ink-400 text-[10px] uppercase tracking-wider">{icon}{label}</div>
            <div className={`font-serif ${small ? "text-sm mt-2" : "text-2xl mt-1"} text-ink-50`}>{value}</div>
        </div>
    );
}

function Step({ n, title, text }) {
    return (
        <div className="flex gap-3">
            <div className="w-8 h-8 shrink-0 rounded-full border border-gold/50 text-gold flex items-center justify-center font-serif">{n}</div>
            <div>
                <div className="text-sm text-ink-50 font-medium">{title}</div>
                <p className="text-xs text-ink-400 mt-1">{text}</p>
            </div>
        </div>
    );
}

function TestForm({ onDone }) {
    const [form, setForm] = useState({
        from: "noreply@idealista.com",
        subject: "Hai ricevuto un nuovo messaggio per il tuo annuncio",
        text: "Nome: Luca Bianchi\nEmail: luca.bianchi@example.com\nTelefono: 333 1234567\nMessaggio: Buongiorno, sono interessato all'immobile. Possiamo organizzare una visita?\nRiferimento: ",
    });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        setBusy(true); setResult(null);
        try { const r = await api.post("/crm/email-parsing/test", form); setResult(r.data); onDone(); }
        catch (e) { setResult({ status: "errore", error: e?.response?.data?.detail || "Errore" }); }
        finally { setBusy(false); }
    };
    return (
        <div className="px-5 pb-5 border-t border-white/10 pt-4 space-y-3">
            <p className="text-xs text-ink-400">Simula la ricezione di un'email da un portale per vedere il flusso completo (estrazione AI → lead nel CRM).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input data-testid="ep-test-from" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} placeholder="Mittente"
                    className="bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
                <input data-testid="ep-test-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Oggetto"
                    className="bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
            </div>
            <textarea data-testid="ep-test-body" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={5}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
            <button data-testid="ep-test-run" onClick={run} disabled={busy} className="cta-gold px-5 py-2.5 text-xs flex items-center gap-2">
                <Send size={13} /> {busy ? "Elaborazione (AI)..." : "Simula ricezione"}
            </button>
            {result && (
                <div data-testid="ep-test-result" className={`text-xs p-3 border ${result.status === "lead_creato" ? "border-green-700/40 text-green-300" : result.status === "duplicata" ? "border-amber-700/40 text-amber-300" : "border-red-700/40 text-red-300"}`}>
                    Esito: {STATUS_LABELS[result.status] || result.status}{result.error ? ` — ${result.error}` : ""}{result.lead_id ? " — lead disponibile nel CRM ✓" : ""}
                </div>
            )}
        </div>
    );
}

function SettingsPanel({ settings, onSaved }) {
    const [s, setS] = useState(settings);
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try { await api.put("/crm/email-parsing/settings", s); onSaved(); } finally { setBusy(false); }
    };
    const Toggle = ({ k, label, hint }) => (
        <div className="flex items-center justify-between gap-4 py-2.5 border-b border-white/5">
            <div>
                <div className="text-sm text-ink-50">{label}</div>
                {hint && <div className="text-[11px] text-ink-500">{hint}</div>}
            </div>
            <button data-testid={`ep-set-${k}`} onClick={() => setS({ ...s, [k]: !s[k] })} role="switch" aria-checked={!!s[k]}
                className={`relative w-10 h-5.5 h-6 rounded-full transition-colors shrink-0 ${s[k] ? "bg-gold" : "bg-ink-700"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink-950 transition-transform ${s[k] ? "translate-x-[18px]" : "translate-x-0.5"}`} />
            </button>
        </div>
    );
    return (
        <div className="px-5 pb-5 border-t border-white/10 pt-3">
            <Toggle k="enabled" label="Email Parsing attivo" hint="Attiva o disattiva l'intero servizio" />
            <Toggle k="auto_import" label="Importazione automatica" hint="Crea i lead automaticamente all'arrivo dell'email" />
            <Toggle k="import_attachments" label="Importa allegati" hint="Salva gli allegati dell'email nel lead" />
            <Toggle k="dedupe_update" label="Controllo duplicati" hint="Se email/telefono esistono già, aggiorna il lead esistente invece di crearne uno nuovo" />
            <Toggle k="notify_email" label="Email di notifica" hint="Ricevi un'email quando viene creato un nuovo lead" />
            <div className="flex items-center justify-between gap-4 py-2.5">
                <div className="text-sm text-ink-50">Conservazione log (giorni)</div>
                <input data-testid="ep-set-retention" type="number" min={7} max={365} value={s.log_retention_days}
                    onChange={(e) => setS({ ...s, log_retention_days: parseInt(e.target.value || "90") })}
                    className="w-24 bg-transparent border border-white/15 px-3 py-1.5 text-sm text-ink-50 focus:border-gold outline-none text-right" />
            </div>
            <button data-testid="ep-set-save" onClick={save} disabled={busy} className="cta-gold px-5 py-2.5 text-xs mt-2">{busy ? "Salvataggio..." : "Salva impostazioni"}</button>
        </div>
    );
}
